import { useEffect, useState } from 'react'
import {
  CERTIFICATE_DATE_CONFIGURATION,
  CERTIFICATE_DATE_FORMAT,
  CERTIFICATE_PRINT_LANGUAGE,
  CERTIFICATE_VALIDITY_TYPE,
  CERTIFICATE_VALIDITY_VISIBILITY,
} from '../../domain/domainCodes'
import { Award } from 'lucide-react'
import { FormDrawer } from '../../components/Drawer'
import { FormBody, FormCols, SwitchField } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { strings as S } from './strings'

const D = S.detail.certificatePanel

const EMPTY = {
  certificateId: '',
  printLanguage: String(CERTIFICATE_PRINT_LANGUAGE.USER_INTERFACE),
  userPrint: false,
  emailSend: false,
  personalEmail: false,
  departmentName: false,
  grade: false,
  gradeA: 90,
  gradeB: 80,
  gradeC: 70,
  dateFormat: String(CERTIFICATE_DATE_FORMAT.GREGORIAN),
  dateConfig: String(CERTIFICATE_DATE_CONFIGURATION.LAST_COURSE),
  /* '' is "never expires" — the legacy's own empty option, and the reason the type is a
     string here: 0 is not a member of CertificateValidityType (Days 1, Months 2, Years 3). */
  validityType: '',
  validity: 0,
  validityVisibility: String(CERTIFICATE_VALIDITY_VISIBILITY.ADMINS_AND_USERS),
}

/**
 * الشهادة — which certificate a programme awards, and how it is printed.
 *
 * `SaveProgramCertificate` takes the template id and a `CertificateOptionsDto` together, so
 * the two are one form. The grade thresholds only mean anything while grades are shown, and
 * appear with the switch that turns them on rather than sitting inert beside it.
 */
export default function ProgramCertificateDrawer({
  open,
  program,
  certificates,
  saving,
  onClose,
  onSave,
}) {
  const [values, setValues] = useState(EMPTY)

  useEffect(() => {
    if (!open) return
    const c = program?.certificate ?? {}
    setValues({
      ...EMPTY,
      certificateId: program?.certificateId ? String(program.certificateId) : '',
      /* Stored as a string on the programme, an int on the wire — see `toCertificateOptions`. */
      printLanguage: String(c.printLanguage || CERTIFICATE_PRINT_LANGUAGE.USER_INTERFACE),
      userPrint: Boolean(c.userPrint),
      emailSend: Boolean(c.emailSend),
      personalEmail: Boolean(c.personalEmail),
      departmentName: Boolean(c.departmentName),
      grade: Boolean(c.grade),
      gradeA: c.gradeA ?? 90,
      gradeB: c.gradeB ?? 80,
      gradeC: c.gradeC ?? 70,
      dateFormat: String(c.dateFormat || CERTIFICATE_DATE_FORMAT.GREGORIAN),
      dateConfig: String(c.dateConfig || CERTIFICATE_DATE_CONFIGURATION.LAST_COURSE),
      validityType: c.validityType ? String(c.validityType) : '',
      validity: c.validity ?? 0,
      validityVisibility: String(c.validityVisibility ?? CERTIFICATE_VALIDITY_VISIBILITY.ADMINS_AND_USERS),
    })
  }, [open, program])

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  return (
    <FormDrawer
      open={open}
      size="md"
      formId="program-certificate"
      icon={Award}
      title={D.title}
      subtitle={program?.name}
      submitLabel={S.form.save}
      cancelLabel={S.close}
      submitting={saving}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault()
        onSave({
          certificateId: Number(values.certificateId),
          certificate: {
            ...values,
            printLanguage: Number(values.printLanguage),
            dateFormat: Number(values.dateFormat),
            dateConfig: Number(values.dateConfig),
            /* Never-expires is null on the wire, not zero. */
            validityType: values.validityType ? Number(values.validityType) : null,
            validity: values.validityType ? Number(values.validity) : null,
            validityVisibility: Number(values.validityVisibility),
          },
        })
      }}
    >
      <FormBody>
        <FormRow label={D.certificate} hint={D.certificateHint}>
          {(id) => (
            <Select
              id={id}
              value={values.certificateId}
              onChange={set('certificateId')}
              options={certificates}
            />
          )}
        </FormRow>

        <FormRow label={D.printLanguage}>
          {(id) => (
            <Select
              id={id}
              value={values.printLanguage}
              onChange={set('printLanguage')}
              options={[
                { value: String(CERTIFICATE_PRINT_LANGUAGE.ARABIC), label: D.languages.arabic },
                { value: String(CERTIFICATE_PRINT_LANGUAGE.ENGLISH), label: D.languages.english },
                { value: String(CERTIFICATE_PRINT_LANGUAGE.USER_INTERFACE), label: D.languages.ui },
              ]}
            />
          )}
        </FormRow>

        {/* Which date the certificate carries, and in which calendar. Both are the legacy's
            own option sets. */}
        <FormRow label={D.dateConfig}>
          {(id) => (
            <Select
              id={id}
              value={values.dateConfig}
              onChange={set('dateConfig')}
              options={[
                { value: String(CERTIFICATE_DATE_CONFIGURATION.LAST_COURSE), label: D.dateConfigs.lastCourse },
                { value: String(CERTIFICATE_DATE_CONFIGURATION.LAST_EXAM), label: D.dateConfigs.lastExam },
                { value: String(CERTIFICATE_DATE_CONFIGURATION.PROGRAM_START_AND_LAST_COURSE), label: D.dateConfigs.programEndLastCourse },
                { value: String(CERTIFICATE_DATE_CONFIGURATION.PROGRAM_START_AND_LAST_EXAM), label: D.dateConfigs.programEndLastExam },
              ]}
            />
          )}
        </FormRow>

        <FormRow label={D.dateFormat}>
          {(id) => (
            <Select
              id={id}
              value={values.dateFormat}
              onChange={set('dateFormat')}
              options={[
                { value: String(CERTIFICATE_DATE_FORMAT.GREGORIAN), label: D.dateFormats.gregorian },
                { value: String(CERTIFICATE_DATE_FORMAT.HIJRI), label: D.dateFormats.hijri },
                { value: String(CERTIFICATE_DATE_FORMAT.BOTH), label: D.dateFormats.both },
                { value: String(CERTIFICATE_DATE_FORMAT.PRINT_LANGUAGE), label: D.dateFormats.ui },
              ]}
            />
          )}
        </FormRow>

        {/* Expiry. The period and who sees it only exist once the certificate expires at
            all, so they appear with the choice that creates them. */}
        <FormRow label={D.validityType}>
          {(id) => (
            <Select
              id={id}
              value={values.validityType}
              onChange={set('validityType')}
              options={[
                { value: '', label: D.validity.never },
                { value: String(CERTIFICATE_VALIDITY_TYPE.DAYS), label: D.validity.days },
                { value: String(CERTIFICATE_VALIDITY_TYPE.MONTHS), label: D.validity.months },
                { value: String(CERTIFICATE_VALIDITY_TYPE.YEARS), label: D.validity.years },
              ]}
            />
          )}
        </FormRow>

        {values.validityType && (
          <FormCols>
            <FormRow label={D.validity.period}>
              {(id) => (
                <TextField
                  id={id}
                  type="number"
                  min={0}
                  max={500}
                  value={String(values.validity)}
                  onChange={(value) => set('validity')(Number(value))}
                />
              )}
            </FormRow>
            <FormRow label={D.validity.visibility}>
              {(id) => (
                <Select
                  id={id}
                  value={values.validityVisibility}
                  onChange={set('validityVisibility')}
                  options={[
                    { value: String(CERTIFICATE_VALIDITY_VISIBILITY.ADMINS_AND_USERS), label: D.validity.adminsAndUsers },
                    { value: String(CERTIFICATE_VALIDITY_VISIBILITY.ADMINS_ONLY), label: D.validity.adminsOnly },
                    { value: String(CERTIFICATE_VALIDITY_VISIBILITY.USERS_ONLY), label: D.validity.usersOnly },
                  ]}
                />
              )}
            </FormRow>
          </FormCols>
        )}

        <SwitchField checked={values.userPrint} onChange={set('userPrint')} label={D.userPrint} />
        <SwitchField checked={values.emailSend} onChange={set('emailSend')} label={D.emailSend} />
        <SwitchField
          checked={values.personalEmail}
          onChange={set('personalEmail')}
          label={D.personalEmail}
        />
        <SwitchField
          checked={values.departmentName}
          onChange={set('departmentName')}
          label={D.departmentName}
        />
        <SwitchField checked={values.grade} onChange={set('grade')} label={D.grade} />

        {/* Only while grades are printed — three thresholds on a certificate that shows no
            grade are three numbers that change nothing. */}
        {values.grade && (
          <FormCols>
            <FormRow label={D.gradeA}>
              {(id) => (
                <TextField
                  id={id}
                  type="number"
                  min={0}
                  max={100}
                  value={String(values.gradeA)}
                  onChange={(value) => set('gradeA')(Number(value))}
                />
              )}
            </FormRow>
            <FormRow label={D.gradeB}>
              {(id) => (
                <TextField
                  id={id}
                  type="number"
                  min={0}
                  max={100}
                  value={String(values.gradeB)}
                  onChange={(value) => set('gradeB')(Number(value))}
                />
              )}
            </FormRow>
            <FormRow label={D.gradeC}>
              {(id) => (
                <TextField
                  id={id}
                  type="number"
                  min={0}
                  max={100}
                  value={String(values.gradeC)}
                  onChange={(value) => set('gradeC')(Number(value))}
                />
              )}
            </FormRow>
          </FormCols>
        )}
      </FormBody>
    </FormDrawer>
  )
}
